import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FiMail, FiRefreshCw, FiCheck } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import Seo from "../components/Seo";

export default function VerifyEmail({ setIsAuthenticated, checkAuth, serviceName }) {
    const canvasRef = useRef(null);
    const animationFrameId = useRef(null);
    const [email, setEmail] = useState("");
    const [countdown, setCountdown] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const navigate = useNavigate();
    const location = useLocation();

    // Фон с частицами (как в Auth.js)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        function setCanvasSize() {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        }
        setCanvasSize();

        const particles = Array.from({ length: 150 }).map(() => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            dx: (Math.random() - 0.5) * 1.2,
            dy: (Math.random() - 0.5) * 1.2,
            size: Math.random() * 3 + 1,
            alpha: Math.random() * 0.5 + 0.1,
        }));

        function animate() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < 120) {
                        ctx.strokeStyle = `rgba(100, 100, 255, ${0.2 - distance / 600})`;
                        ctx.lineWidth = 0.8;
                        ctx.beginPath();
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.stroke();
                    }
                }
            }

            particles.forEach((p) => {
                p.x += p.dx;
                p.y += p.dy;

                if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
                if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

                ctx.fillStyle = `rgba(100, 150, 255, ${p.alpha})`;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();
            });

            animationFrameId.current = requestAnimationFrame(animate);
        }
        animate();

        window.addEventListener("resize", setCanvasSize);
        return () => {
            window.removeEventListener("resize", setCanvasSize);
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        };
    }, []);

    // Получаем email из localStorage или параметров
    useEffect(() => {
        const storedEmail = localStorage.getItem("pendingVerificationEmail");
        const params = new URLSearchParams(location.search);
        const emailParam = params.get("email");
        
        if (emailParam) {
            setEmail(emailParam);
            localStorage.setItem("pendingVerificationEmail", emailParam);
        } else if (storedEmail) {
            setEmail(storedEmail);
        }
    }, [location]);

    // Таймер для повторной отправки
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        } else {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    // Проверяем статус верификации периодически
    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const res = await fetch("/api/me", {
                    credentials: "include",
                });
                
                if (res.ok) {
                    const userData = await res.json();
                    if (userData.is_verified) {
                        clearInterval(interval);
                        localStorage.removeItem("pendingVerificationEmail");
                        setIsAuthenticated(true);
                        navigate("/dashboard");
                    }
                }
            } catch (err) {
                console.error("Ошибка проверки статуса:", err);
            }
        }, 5000); // Проверяем каждые 5 секунд

        return () => clearInterval(interval);
    }, [navigate, setIsAuthenticated]);

    const handleResendEmail = async () => {
        if (!email || !canResend) return;

        setLoading(true);
        setMessage("");
        
        try {
            const res = await fetch("/api/resend-verification", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email }),
            });

            const data = await res.json();
            
            setMessage(data.message || "");
            setMessageType(data.type || "info");
            
            if (res.ok) {
                setCountdown(60);
                setCanResend(false);
            }
        } catch (err) {
            setMessage("Ошибка сети: " + err.message);
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch("/api/logout", {
                method: "POST",
                credentials: "include",
            });
            localStorage.removeItem("pendingVerificationEmail");
            setIsAuthenticated(false);
            navigate("/auth");
        } catch (err) {
            console.error("Ошибка выхода:", err);
        }
    };

    return (
        <>
            <Seo
                title={`${serviceName} - Подтверждение email`}
                description="Подтвердите ваш email для завершения регистрации"
            />
            <div className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
                <canvas ref={canvasRef} className="absolute inset-0 z-0" />
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-purple-900/30 to-black/50"></div>

                <section className="relative z-10 flex items-center justify-center h-screen px-4">
                    <motion.div
                        className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl shadow-xl border border-white/20 max-w-md w-full"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="text-center mb-6">
                            <FiMail className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                            <h1 className="text-2xl font-bold mb-2">Подтвердите вашу почту</h1>
                            <p className="text-gray-300">
                                Мы отправили письмо с ссылкой для подтверждения на:
                            </p>
                            <p className="font-semibold text-lg mt-2 text-blue-300">{email}</p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-blue-900/20 p-4 rounded-xl border border-blue-700/50">
                                <h3 className="font-semibold mb-2 flex items-center gap-2">
                                    <FiCheck className="text-green-400" />
                                    Что делать дальше:
                                </h3>
                                <ul className="text-sm text-gray-300 space-y-1 ml-6 list-disc">
                                    <li>Проверьте папку "Входящие" или "Спам"</li>
                                    <li>Найдите письмо от <strong>{serviceName}</strong></li>
                                    <li>Нажмите на ссылку в письме</li>
                                    <li>После подтверждения вы будете перенаправлены в личный кабинет</li>
                                </ul>
                            </div>

                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleResendEmail}
                                    disabled={!canResend || loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FiRefreshCw className={loading ? "animate-spin" : ""} />
                                    {loading ? "Отправка..." : "Отправить повторно"}
                                </button>
                                
                                {!canResend && (
                                    <span className="text-sm text-gray-400">
                                        Повторно через: {countdown}с
                                    </span>
                                )}
                            </div>

                            {message && (
                                <p className={`text-center text-sm ${messageType === "info" ? "text-green-400" : messageType === "error" ? "text-red-500" : "text-yellow-400"}`}>
                                    {message}
                                </p>
                            )}

                            <button
                                onClick={() => navigate("/dashboard")}
                                className="w-full py-2 text-center text-blue-400 hover:text-blue-300 transition"
                            >
                                Я уже подтвердил email
                            </button>

                            <div className="pt-4 border-t border-white/20">
                                <button
                                    onClick={handleLogout}
                                    className="w-full py-2 text-center text-gray-400 hover:text-white transition"
                                >
                                    Выйти и войти с другим email
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </section>
            </div>
        </>
    );
}
